import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "../utils/axiosInstance";
import {
  Modal,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  InputGroup,
  Dropdown,
  DropdownButton,
  OverlayTrigger,
  Tooltip,
  Offcanvas,
  Collapse,
  Button,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import InkungaModal from "../components/InkungaModal";

import { getApiBase } from "../utils/useApiBase";
const API_URL = `${getApiBase()}/inkunga_leta`;

export default function AbaturageBafiteInkunguzaleta() {
  // ════════════════════════════════════════════════════════════════════════
  // STATES
  // ════════════════════════════════════════════════════════════════════════
  const [form, setForm] = useState({
    umuturage: "",
    ubwoko: "",
    amount: "",
    itariki: "",
    user_id: "",
  });

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRange, setFilterRange] = useState("all");
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });

  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [viewMode, setViewMode] = useState("auto");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Device Detection
  const [deviceInfo, setDeviceInfo] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024,
    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
    isAndroid: /Android/.test(navigator.userAgent),
    touchDevice: "ontouchstart" in window,
  });

  const containerRef = useRef(null);

  // ════════════════════════════════════════════════════════════════════════
  // THEME
  // ════════════════════════════════════════════════════════════════════════
  const t = {
    bg: "#F5F7FB",
    bgAlt: "#eef2f9",
    card: "#ffffff",
    surface: "#f8fafc",
    text: "#0B1F3B",
    textBright: "#0B1F3B",
    textSecondary: "#475569",
    muted: "#64748b",
    // Government blue — primary
    primary: "#0B3D91",
    primarySolid: "#0B3D91",
    primaryGlow: "rgba(11, 61, 145, 0.22)",
    primarySoft: "rgba(11, 61, 145, 0.08)",
    // Amber — accent
    accent: "#b45309",
    accentSolid: "#b45309",
    accentGlow: "rgba(217, 119, 6, 0.20)",
    accentSoft: "rgba(217, 119, 6, 0.10)",
    // Semantic
    success: "#16a34a",
    successSolid: "#15803d",
    successGlow: "rgba(22, 163, 74, 0.20)",
    successSoft: "rgba(22, 163, 74, 0.10)",
    warning: "#d97706",
    warningSolid: "#b45309",
    warningGlow: "rgba(217, 119, 6, 0.20)",
    warningSoft: "rgba(217, 119, 6, 0.10)",
    danger: "#dc2626",
    dangerSolid: "#b91c1c",
    dangerGlow: "rgba(220, 38, 38, 0.20)",
    dangerSoft: "rgba(220, 38, 38, 0.10)",
    info: "#0369a1",
    infoSolid: "#075985",
    infoGlow: "rgba(3, 105, 161, 0.20)",
    infoSoft: "rgba(3, 105, 161, 0.10)",
    border: "#e2e8f0",
    borderLight: "#cbd5e1",
    // Government gradients (used sparingly — header bar only)
    grad1: "linear-gradient(135deg, #0B3D91 0%, #093278 100%)",
    grad2: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
    grad3: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
    grad4: "linear-gradient(135deg, #0369a1 0%, #075985 100%)",
    gradHeader: "linear-gradient(135deg, #0B3D91 0%, #093278 100%)",
  };

  // ════════════════════════════════════════════════════════════════════════
  // DEVICE DETECTION & RESPONSIVE HANDLER
  // ════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setDeviceInfo({
        width,
        height,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
        isAndroid: /Android/.test(navigator.userAgent),
        touchDevice: "ontouchstart" in window,
        isSmallPhone: width < 360,
        isMediumPhone: width >= 360 && width < 400,
        isLargePhone: width >= 400 && width < 480,
        isPhablet: width >= 480 && width < 768,
        isLandscape: width > height,
      });

      // Auto adjust items per page
      if (width < 360) {
        setItemsPerPage(4);
      } else if (width < 576) {
        setItemsPerPage(6);
      } else if (width < 768) {
        setItemsPerPage(8);
      } else if (width < 1024) {
        setItemsPerPage(10);
      } else {
        setItemsPerPage(12);
      }
    };

    updateDeviceInfo();
    window.addEventListener("resize", updateDeviceInfo);
    window.addEventListener("orientationchange", () => setTimeout(updateDeviceInfo, 100));

    return () => {
      window.removeEventListener("resize", updateDeviceInfo);
      window.removeEventListener("orientationchange", updateDeviceInfo);
    };
  }, []);

  // ════════════════════════════════════════════════════════════════════════
  // DATA FUNCTIONS
  // ════════════════════════════════════════════════════════════════════════
  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_URL);
      setRecords(res.data || []);
    } catch {
      showAlertMsg("Habaye ikibazo mu gushaka amakuru.", "danger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const showAlertMsg = useCallback((message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 4500);
  }, []);

  const formatRWF = (amount) =>
    new Intl.NumberFormat("rw-RW", { 
      style: "currency", 
      currency: "RWF",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));

  const formatRWFShort = (amount) => {
    const num = Number(amount || 0);
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M RWF`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K RWF`;
    }
    return formatRWF(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString("rw-RW", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Statistics
  const stats = useMemo(() => {
    const totalAmount = records.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    const totalRecipients = records.length;
    const avgAmount = totalRecipients > 0 ? totalAmount / totalRecipients : 0;
    const thisMonth = records.filter((r) => {
      if (!r.itariki) return false;
      const d = new Date(r.itariki);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;

    return { totalAmount, totalRecipients, avgAmount, thisMonth };
  }, [records]);

  // Filtering
  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const term = searchTerm.toLowerCase();
      const matchSearch =
        record.umuturage?.toLowerCase().includes(term) ||
        record.ubwoko?.toLowerCase().includes(term);
      if (!matchSearch) return false;
      if (!record.itariki) return filterRange === "all";
      const recDate = new Date(record.itariki);
      const today = new Date();
      if (filterRange === "today") {
        return recDate.toDateString() === today.toDateString();
      }
      if (filterRange === "month") {
        return (
          recDate.getMonth() === today.getMonth() &&
          recDate.getFullYear() === today.getFullYear()
        );
      }
      return true;
    });
  }, [records, searchTerm, filterRange]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredRecords.slice(startIndex, startIndex + itemsPerPage);

  // ════════════════════════════════════════════════════════════════════════
  // FORM HANDLERS
  // ════════════════════════════════════════════════════════════════════════
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setForm({ umuturage: "", ubwoko: "", amount: "", itariki: "", user_id: "" });
    setEditing(false);
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/${editId}`, form);
        showAlertMsg("Inkunga yavuguruwe neza!", "success");
      } else {
        await axios.post(API_URL, form);
        showAlertMsg("Inkunga yanditswe neza!", "success");
      }
      resetForm();
      setShow(false);
      fetchRecords();
    } catch {
      showAlertMsg("Ntibyakunze kubika. Ongera ugerageze.", "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = () => {
    resetForm();
    setShow(true);
  };

  const handleEdit = (item) => {
    setForm({
      umuturage: item.umuturage || "",
      ubwoko: item.ubwoko || "",
      amount: item.amount || "",
      itariki: item.itariki ? item.itariki.split("T")[0] : "",
      user_id: item.user_id || "",
    });
    setEditId(item.id);
    setEditing(true);
    setShow(true);
  };

  const handleDeleteClick = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/${deleteTarget.id}`);
      showAlertMsg("Inkunga yasibwe neza!", "success");
      fetchRecords();
    } catch {
      showAlertMsg("Ntibikunze gusiba!", "danger");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const openModal = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const closeModal = () => {
    setSelectedRecord(null);
    setShowDetailModal(false);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterRange("all");
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  const exportToCSV = () => {
    const headers = ["No", "Umuturage", "Ubwoko", "Agaciro", "Itariki"];
    const csvData = filteredRecords.map((i, idx) => [
      idx + 1,
      `"${i.umuturage || ""}"`,
      `"${i.ubwoko || "-"}"`,
      i.amount || "0",
      i.itariki ? new Date(i.itariki).toLocaleDateString("rw-RW") : "-",
    ]);
    const csv = [headers.join(","), ...csvData.map((r) => r.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `inkunga_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showAlertMsg("Dosiye yakoboye neza!", "success");
  };

  const getFilterLabel = () => {
    const labels = { all: "Byose", today: "Uyu munsi", month: "Ukwezi" };
    return labels[filterRange] || "Byose";
  };

  // ════════════════════════════════════════════════════════════════════════
  // STAT CARDS DATA
  // ════════════════════════════════════════════════════════════════════════
  const statCards = useMemo(
    () => [
      {
        title: deviceInfo.width < 400 ? "Agaciro" : "Agaciro Kose",
        val: deviceInfo.width < 400 ? formatRWFShort(stats.totalAmount) : formatRWF(stats.totalAmount),
        gradient: t.grad1,
        glow: t.primaryGlow,
        soft: t.primarySoft,
        icon: "cash-stack",
        accent: t.primary,
        pct: 100,
      },
      {
        title: deviceInfo.width < 400 ? "Abahawe" : "Abahawe Bose",
        val: stats.totalRecipients,
        gradient: t.grad2,
        glow: t.successGlow,
        soft: t.successSoft,
        icon: "people-fill",
        accent: t.success,
        pct: 100,
      },
      {
        title: deviceInfo.width < 400 ? "Gakunda" : "Agaciro Gakunda",
        val: deviceInfo.width < 400 ? formatRWFShort(stats.avgAmount) : formatRWF(stats.avgAmount),
        gradient: t.grad3,
        glow: t.warningGlow,
        soft: t.warningSoft,
        icon: "graph-up-arrow",
        accent: t.warning,
        pct: stats.totalAmount > 0 ? Math.round((stats.avgAmount / stats.totalAmount) * 100) : 0,
      },
      {
        title: deviceInfo.width < 400 ? "Ukwezi" : "Mu Kwezi",
        val: stats.thisMonth,
        gradient: t.grad4,
        glow: t.infoGlow,
        soft: t.infoSoft,
        icon: "calendar-check-fill",
        accent: t.info,
        pct: stats.totalRecipients > 0 ? Math.round((stats.thisMonth / stats.totalRecipients) * 100) : 0,
      },
    ],
    [stats, deviceInfo.width, t]
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER MOBILE CARDS (< 576px)
  // ════════════════════════════════════════════════════════════════════════
  const renderMobileCards = () => (
    <div className="mobile-cards-grid">
      {paginatedData.map((item, index) => {
        const isExpanded = expandedCard === item.id;
        return (
          <div key={item.id} className="mobile-inkunga-card">
            {/* Card Header */}
            <div
              className="mic-header"
              onClick={() => setExpandedCard(isExpanded ? null : item.id)}
            >
              <div className="mic-header-left">
                <span className="mic-number">#{startIndex + index + 1}</span>
                <div className="mic-avatar">
                  {item.umuturage?.charAt(0)?.toUpperCase() || "?"}
                </div>
              </div>
              <div className="mic-header-center">
                <h6 className="mic-name">{item.umuturage}</h6>
                <div className="mic-badges">
                  <span className="mic-type-badge">
                    <i className="bi bi-tag-fill"></i>
                    {item.ubwoko || "—"}
                  </span>
                </div>
              </div>
              <div className="mic-expand-icon">
                <i className={`bi bi-chevron-${isExpanded ? "up" : "down"}`}></i>
              </div>
            </div>

            {/* Card Body - Always Visible */}
            <div className="mic-body">
              <div className="mic-main-info">
                <div className="mic-amount">
                  <span className="mic-amount-label">Agaciro</span>
                  <span className="mic-amount-value">{formatRWF(item.amount)}</span>
                </div>
                <div className="mic-date">
                  <i className="bi bi-calendar3"></i>
                  {formatDate(item.itariki)}
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            <Collapse in={isExpanded}>
              <div className="mic-expanded">
                <div className="mic-detail-row">
                  <span className="mic-detail-label">Ubwoko bw'Inkunga:</span>
                  <span className="mic-detail-value">{item.ubwoko || "—"}</span>
                </div>
                <div className="mic-detail-row">
                  <span className="mic-detail-label">Itariki yuzuye:</span>
                  <span className="mic-detail-value">
                    {item.itariki
                      ? new Date(item.itariki).toLocaleDateString("rw-RW", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "—"}
                  </span>
                </div>
              </div>
            </Collapse>

            {/* Card Actions */}
            <div className="mic-actions">
              <button className="mic-action-btn view" onClick={() => openModal(item)}>
                <i className="bi bi-eye"></i>
                <span>Reba</span>
              </button>
              <button className="mic-action-btn edit" onClick={() => handleEdit(item)}>
                <i className="bi bi-pencil"></i>
                <span>Hindura</span>
              </button>
              <button className="mic-action-btn delete" onClick={() => handleDeleteClick(item)}>
                <i className="bi bi-trash"></i>
                <span>Siba</span>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER TABLET LIST (576px - 767px)
  // ════════════════════════════════════════════════════════════════════════
  const renderTabletList = () => (
    <div className="tablet-list-view">
      {paginatedData.map((item, index) => (
        <div key={item.id} className="tablet-inkunga-item">
          <div className="tii-left">
            <span className="tii-number">{startIndex + index + 1}</span>
            <div className="tii-avatar">
              {item.umuturage?.charAt(0)?.toUpperCase() || "?"}
            </div>
          </div>

          <div className="tii-content">
            <div className="tii-header">
              <h6 className="tii-name">{item.umuturage}</h6>
              <span className="tii-amount">{formatRWF(item.amount)}</span>
            </div>
            <div className="tii-details">
              <span className="tii-detail">
                <i className="bi bi-tag-fill"></i>
                {item.ubwoko || "—"}
              </span>
              <span className="tii-detail">
                <i className="bi bi-calendar3"></i>
                {formatDate(item.itariki)}
              </span>
            </div>
          </div>

          <div className="tii-actions">
            <button className="tii-btn view" onClick={() => openModal(item)}>
              <i className="bi bi-eye"></i>
            </button>
            <button className="tii-btn edit" onClick={() => handleEdit(item)}>
              <i className="bi bi-pencil"></i>
            </button>
            <button className="tii-btn delete" onClick={() => handleDeleteClick(item)}>
              <i className="bi bi-trash"></i>
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER DESKTOP TABLE (768px+)
  // ════════════════════════════════════════════════════════════════════════
  const renderDesktopTable = () => (
    <div className="table-responsive-wrapper">
      <table className="modern-inkunga-table">
        <thead>
          <tr>
            <th className="th-number">#</th>
            <th className="th-name">Umuturage</th>
            <th className="th-type">Ubwoko bw'Inkunga</th>
            <th className="th-amount">Agaciro (RWF)</th>
            <th className="th-date">Itariki</th>
            <th className="th-actions">Ibikorwa</th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((item, index) => (
            <tr key={item.id}>
              <td className="td-number">
                <span className="row-number">{startIndex + index + 1}</span>
              </td>
              <td className="td-name">
                <div className="name-cell">
                  <div className="name-avatar">
                    {item.umuturage?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <span className="name-text">{item.umuturage}</span>
                </div>
              </td>
              <td className="td-type">
                <span className="type-badge">
                  <i className="bi bi-tag-fill"></i>
                  {item.ubwoko || "—"}
                </span>
              </td>
              <td className="td-amount">
                <span className="amount-text">{formatRWF(item.amount)}</span>
              </td>
              <td className="td-date">
                <span className="date-text">
                  <i className="bi bi-calendar3"></i>
                  {formatDate(item.itariki)}
                </span>
              </td>
              <td className="td-actions">
                <div className="action-btns">
                  <OverlayTrigger placement="top" overlay={<Tooltip>Reba</Tooltip>}>
                    <button className="action-btn view" onClick={() => openModal(item)}>
                      <i className="bi bi-eye"></i>
                    </button>
                  </OverlayTrigger>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Hindura</Tooltip>}>
                    <button className="action-btn edit" onClick={() => handleEdit(item)}>
                      <i className="bi bi-pencil"></i>
                    </button>
                  </OverlayTrigger>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Siba</Tooltip>}>
                    <button className="action-btn delete" onClick={() => handleDeleteClick(item)}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </OverlayTrigger>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // RENDER DATA VIEW
  // ════════════════════════════════════════════════════════════════════════
  const renderDataView = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <Spinner animation="border" style={{ color: t.primarySolid }} />
          <p>Gutegereza...</p>
        </div>
      );
    }

    if (filteredRecords.length === 0) {
      return (
        <div className="empty-state">
          <i className="bi bi-gift"></i>
          <h4>Nta nkunga zihari</h4>
          <p>
            {searchTerm || filterRange !== "all"
              ? "Nta makuru ahuye n'ibyo ushakisha"
              : "Tangira ukongeramo inkunga nshya"}
          </p>
          <button className="btn-primary-custom" onClick={handleAdd}>
            <i className="bi bi-plus-circle me-2"></i>
            Ongeraho Inkunga
          </button>
        </div>
      );
    }

    const { width } = deviceInfo;

    if (viewMode === "cards" || (viewMode === "auto" && width < 576)) {
      return renderMobileCards();
    } else if (viewMode === "list" || (viewMode === "auto" && width >= 576 && width < 768)) {
      return renderTabletList();
    } else {
      return renderDesktopTable();
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // RENDER PAGINATION
  // ════════════════════════════════════════════════════════════════════════
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const { width } = deviceInfo;
    const maxVisible = width < 400 ? 3 : width < 576 ? 5 : 7;

    let pages = [];
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper">
        <div className="pagination-info">
          <span>
            {width < 400
              ? `${startIndex + 1}-${Math.min(startIndex + itemsPerPage, filteredRecords.length)}`
              : `Igaragaza ${startIndex + 1} - ${Math.min(
                  startIndex + itemsPerPage,
                  filteredRecords.length
                )} kuri ${filteredRecords.length}`}
          </span>
        </div>

        <div className="pagination-controls">
          <button
            className="page-btn nav-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(1)}
          >
            <i className="bi bi-chevron-double-left"></i>
          </button>
          <button
            className="page-btn nav-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            <i className="bi bi-chevron-left"></i>
          </button>

          {start > 1 && (
            <>
              <button className="page-btn" onClick={() => setCurrentPage(1)}>
                1
              </button>
              {start > 2 && <span className="page-ellipsis">...</span>}
            </>
          )}

          {pages.map((page) => (
            <button
              key={page}
              className={`page-btn ${page === currentPage ? "active" : ""}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}

          {end < totalPages && (
            <>
              {end < totalPages - 1 && <span className="page-ellipsis">...</span>}
              <button className="page-btn" onClick={() => setCurrentPage(totalPages)}>
                {totalPages}
              </button>
            </>
          )}

          <button
            className="page-btn nav-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
          <button
            className="page-btn nav-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            <i className="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div
      ref={containerRef}
      className={`app-container ${deviceInfo.isIOS ? "ios" : ""} ${
        deviceInfo.isAndroid ? "android" : ""
      }`}
      style={{ background: t.bg, color: t.text }}
    >
      {/* ══════════════════════════════════════════════════════════════════
          COMPREHENSIVE RESPONSIVE STYLES
      ══════════════════════════════════════════════════════════════════ */}
      <style>{`
        /* ═══════════════════════════════════════════════════════════════════
           CSS CUSTOM PROPERTIES
        ═══════════════════════════════════════════════════════════════════ */
        :root {
          --bg: #F5F7FB;
          --bg-alt: #eef2f9;
          --card: #ffffff;
          --text: #0B1F3B;
          --text-bright: #0B1F3B;
          --text-secondary: #475569;
          --muted: #64748b;
          /* Government blue */
          --primary: #0B3D91;
          --primary-solid: #0B3D91;
          --primary-glow: rgba(11, 61, 145, 0.22);
          --primary-soft: rgba(11, 61, 145, 0.08);
          /* Amber accent */
          --accent: #b45309;
          --accent-solid: #b45309;
          --accent-glow: rgba(217, 119, 6, 0.20);
          --accent-soft: rgba(217, 119, 6, 0.10);
          /* Semantic */
          --success: #16a34a;
          --success-solid: #15803d;
          --success-glow: rgba(22, 163, 74, 0.20);
          --success-soft: rgba(22, 163, 74, 0.10);
          --warning: #d97706;
          --warning-solid: #b45309;
          --warning-glow: rgba(217, 119, 6, 0.20);
          --warning-soft: rgba(217, 119, 6, 0.10);
          --danger: #dc2626;
          --danger-solid: #b91c1c;
          --danger-glow: rgba(220, 38, 38, 0.20);
          --danger-soft: rgba(220, 38, 38, 0.10);
          --info: #0369a1;
          --info-solid: #075985;
          --info-glow: rgba(3, 105, 161, 0.20);
          --info-soft: rgba(3, 105, 161, 0.10);
          --border: #e2e8f0;
          --border-light: #cbd5e1;

          --radius-sm: 8px;
          --radius-md: 12px;
          --radius-lg: 16px;
          --radius-xl: 20px;
          
          --font-xs: clamp(0.65rem, 2vw, 0.75rem);
          --font-sm: clamp(0.75rem, 2.5vw, 0.875rem);
          --font-md: clamp(0.875rem, 3vw, 1rem);
          --font-lg: clamp(1rem, 3.5vw, 1.25rem);
          --font-xl: clamp(1.25rem, 4vw, 1.5rem);
          --font-2xl: clamp(1.5rem, 5vw, 2rem);
          
          --space-xs: clamp(0.25rem, 1vw, 0.5rem);
          --space-sm: clamp(0.5rem, 2vw, 0.75rem);
          --space-md: clamp(0.75rem, 3vw, 1rem);
          --space-lg: clamp(1rem, 4vw, 1.5rem);
          --space-xl: clamp(1.5rem, 5vw, 2rem);
        }

        /* ═══════════════════════════════════════════════════════════════════
           BASE STYLES
        ═══════════════════════════════════════════════════════════════════ */
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800;900&display=swap');
        
        *, *::before, *::after {
          box-sizing: border-box;
          -webkit-tap-highlight-color: transparent;
        }

        html {
          -webkit-text-size-adjust: 100%;
          scroll-behavior: smooth;
        }

        body {
          font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          
        }

        /* ═══════════════════════════════════════════════════════════════════
           APP CONTAINER
        ═══════════════════════════════════════════════════════════════════ */
        .app-container {
          min-height: 100vh;
          min-height: 100dvh;
          padding: var(--space-md);
          padding-top: max(var(--space-md), env(safe-area-inset-top));
          padding-bottom: max(var(--space-md), env(safe-area-inset-bottom));
          padding-left: max(var(--space-md), env(safe-area-inset-left));
          padding-right: max(var(--space-md), env(safe-area-inset-right));
          overflow-x: hidden;
        }

        .app-container.ios {
          -webkit-overflow-scrolling: touch;
        }

        /* ═══════════════════════════════════════════════════════════════════
           PAGE HEADER
        ═══════════════════════════════════════════════════════════════════ */
        .header-section {
          margin-bottom: var(--space-lg);
        }

        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: var(--space-sm);
        }

        .header-title {
          font-size: var(--font-lg);
          font-weight: 700;
          color: var(--primary-solid);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          line-height: 1.3;
          margin: 0;
        }

        .header-title i {
          font-size: 1.1em;
        }

        .header-subtitle {
          font-size: var(--font-sm);
          color: var(--muted);
          line-height: 1.4;
          margin: 0.25rem 0 0;
        }

        .header-actions {
          display: flex;
          gap: var(--space-xs);
          flex-wrap: wrap;
        }

        .btn-primary-modern {
          background: var(--primary-solid) !important;
          border: none !important;
          border-radius: var(--radius-md) !important;
          padding: var(--space-sm) var(--space-md) !important;
          font-weight: 600 !important;
          font-size: var(--font-sm) !important;
          color: #fff !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 0.4rem !important;
          white-space: nowrap !important;
          transition: all 0.2s ease !important;
        }

        .btn-primary-modern:hover {
          background: #093278 !important;
        }

        .btn-primary-modern:active {
          transform: scale(0.97) !important;
        }

        .btn-outline-modern {
          background: #ffffff !important;
          border: 1px solid var(--border) !important;
          color: var(--text-secondary) !important;
          border-radius: var(--radius-md) !important;
          padding: var(--space-sm) var(--space-md) !important;
          font-size: var(--font-sm) !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 0.3rem !important;
          transition: all 0.2s ease !important;
        }

        .btn-outline-modern:hover {
          background: var(--primary-soft) !important;
          border-color: var(--primary-solid) !important;
          color: var(--primary-solid) !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           STATS GRID
        ═══════════════════════════════════════════════════════════════════ */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: var(--space-sm);
          margin-bottom: var(--space-lg);
        }

        .stat-card {
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: var(--space-md);
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          border-color: var(--primary-solid);
          box-shadow: 0 2px 8px rgba(11,61,145,0.08);
        }

        .stat-icon {
          width: clamp(36px, 10vw, 48px);
          height: clamp(36px, 10vw, 48px);
          border-radius: 50%;
          background: var(--primary-soft);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-icon i {
          font-size: clamp(1rem, 4vw, 1.35rem);
          color: var(--stat-color, var(--primary-solid));
        }

        .stat-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: clamp(1.15rem, 5vw, 1.6rem);
          font-weight: 800;
          color: var(--text-bright);
          line-height: 1.1;
        }

        .stat-label {
          font-size: var(--font-xs);
          color: var(--muted);
          text-transform: uppercase;
          letter-spacing: 0.04em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .stat-percent {
          font-size: var(--font-xs);
          font-weight: 600;
        }

        @media (max-width: 1279px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 575px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 0.6rem; }
          .stat-card { padding: 0.65rem; }
          .header-title { font-size: 1.1rem; }
        }

        /* ═══════════════════════════════════════════════════════════════════
           BUTTONS (non-header)
        ═══════════════════════════════════════════════════════════════════ */
        .btn-primary-custom {
          background: var(--primary-solid);
          border: none;
          border-radius: var(--radius-md);
          padding: var(--space-sm) var(--space-md);
          font-weight: 600;
          font-size: var(--font-sm);
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          box-shadow: 0 2px 6px rgba(11,61,145,0.25);
        }
        .btn-primary-custom:hover { background: #093278; box-shadow: 0 4px 10px rgba(11,61,145,0.3); }
        .btn-primary-custom:active { transform: scale(0.97); }

        .btn-outline-custom {
          background: #ffffff;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: var(--space-sm) var(--space-md);
          font-size: var(--font-sm);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
        }
        .btn-outline-custom:hover { border-color: var(--primary-solid); color: var(--primary-solid); background: var(--primary-soft); }
        .btn-outline-custom.success { color: var(--success); }
        .btn-outline-custom.success:hover { border-color: var(--success-solid); background: var(--success-soft); }

        /* ═══════════════════════════════════════════════════════════════════
           FILTERS
        ═══════════════════════════════════════════════════════════════════ */
        .filter-section {
          margin-bottom: var(--space-md);
        }

        .filter-card {
          background: var(--card);
          border-radius: var(--radius-lg);
          padding: var(--space-md);
          border: 1px solid var(--border);
          box-shadow: 0 1px 3px rgba(11,61,145,0.05);
        }

        .filter-row {
          display: flex;
          gap: var(--space-sm);
          flex-wrap: wrap;
        }

        .search-wrapper {
          flex: 1;
          min-width: 180px;
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--muted);
          pointer-events: none;
        }

        .search-input {
          width: 100%;
          background: #ffffff !important;
          border: 1px solid var(--border) !important;
          color: var(--text) !important;
          border-radius: var(--radius-md) !important;
          padding: var(--space-sm) var(--space-sm) var(--space-sm) 2.25rem !important;
          font-size: var(--font-sm) !important;
        }

        .search-input:focus {
          border-color: var(--primary-solid) !important;
          box-shadow: 0 0 0 3px var(--primary-soft) !important;
        }

        .search-input::placeholder {
          color: var(--muted) !important;
          opacity: 0.6;
        }

        .filter-info {
          margin-top: var(--space-sm);
          font-size: var(--font-xs);
          color: var(--muted);
        }

        .mobile-filter-btn {
          display: none;
        }

        /* ═══════════════════════════════════════════════════════════════════
           DATA CONTAINER
        ═══════════════════════════════════════════════════════════════════ */
        .data-container {
          background: var(--card);
          border-radius: var(--radius-xl);
          border: 1px solid var(--border);
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(11,61,145,0.05);
        }

        .data-header {
          background: #f8fafc;
          padding: var(--space-md);
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: var(--space-sm);
        }

        .data-title {
          font-size: var(--font-md);
          font-weight: 700;
          color: var(--text-bright);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
        }

        .data-title i {
          color: var(--primary-solid);
        }

        .data-meta {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }

        .results-count {
          font-size: var(--font-xs);
          color: var(--muted);
        }

        .view-toggle {
          display: none;
          gap: 3px;
          background: #ffffff;
          border: 1px solid var(--border);
          padding: 3px;
          border-radius: var(--radius-sm);
        }

        .view-toggle-btn {
          background: transparent;
          border: none;
          color: var(--muted);
          padding: 0.35rem 0.5rem;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .view-toggle-btn.active {
          background: var(--primary-solid);
          color: white;
        }

        /* ═══════════════════════════════════════════════════════════════════
           LOADING & EMPTY STATES
        ═══════════════════════════════════════════════════════════════════ */
        .loading-state,
        .empty-state {
          padding: var(--space-xl) var(--space-md);
          text-align: center;
        }

        .loading-state p,
        .empty-state h4 {
          color: var(--muted);
          margin-top: var(--space-md);
        }

        .empty-state i {
          font-size: clamp(3rem, 15vw, 5rem);
          color: var(--border-light);
        }

        .empty-state p {
          color: var(--muted);
          margin-bottom: var(--space-lg);
        }

        /* ═══════════════════════════════════════════════════════════════════
           MOBILE CARDS (< 576px)
        ═══════════════════════════════════════════════════════════════════ */
        .mobile-cards-grid {
          padding: var(--space-sm);
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
        }

        .mobile-inkunga-card {
          background: var(--card);
          border: 1px solid var(--border);
          border-left: 3px solid var(--primary-solid);
          border-radius: var(--radius-lg);
          overflow: hidden;
        }

        .mic-header {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-sm);
          background: #f8fafc;
          cursor: pointer;
        }

        .mic-header-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .mic-number {
          font-size: 0.6rem;
          color: var(--muted);
          font-weight: 700;
        }

        .mic-avatar {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-md);
          background: var(--primary-solid);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          font-weight: 800;
          color: white;
        }

        .mic-header-center {
          flex: 1;
          min-width: 0;
        }

        .mic-name {
          font-size: var(--font-sm);
          font-weight: 700;
          color: var(--text-bright);
          margin: 0 0 0.25rem 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .mic-badges {
          display: flex;
          gap: 0.25rem;
          flex-wrap: wrap;
        }

        .mic-type-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.2rem 0.5rem;
          border-radius: 12px;
          font-size: var(--font-xs);
          font-weight: 600;
          color: var(--accent-solid);
          background: var(--accent-soft);
          border: 1px solid rgba(217,119,6,0.25);
        }

        .mic-type-badge i {
          font-size: 0.6rem;
        }

        .mic-expand-icon {
          color: var(--muted);
          font-size: 0.85rem;
          padding: 0.25rem;
        }

        .mic-body {
          padding: var(--space-sm);
          border-top: 1px solid var(--border);
        }

        .mic-main-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .mic-amount {
          display: flex;
          flex-direction: column;
        }

        .mic-amount-label {
          font-size: var(--font-xs);
          color: var(--muted);
        }

        .mic-amount-value {
          font-size: var(--font-md);
          font-weight: 800;
          color: var(--success);
        }

        .mic-date {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: var(--font-xs);
          color: var(--text-secondary);
        }

        .mic-date i {
          font-size: 0.7rem;
          color: var(--muted);
        }

        .mic-expanded {
          padding: var(--space-sm);
          background: #f8fafc;
          border-top: 1px dashed var(--border);
        }

        .mic-detail-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 0.35rem 0;
          gap: var(--space-sm);
        }

        .mic-detail-label {
          font-size: var(--font-xs);
          color: var(--muted);
          flex-shrink: 0;
        }

        .mic-detail-value {
          font-size: var(--font-xs);
          color: var(--text);
          text-align: right;
        }

        .mic-actions {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          border-top: 1px solid var(--border);
        }

        .mic-action-btn {
          border: none;
          background: transparent;
          padding: var(--space-sm);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.15rem;
          cursor: pointer;
          transition: all 0.2s;
          font-size: var(--font-xs);
        }

        .mic-action-btn i {
          font-size: 1rem;
        }

        .mic-action-btn.view {
          color: var(--info);
          border-right: 1px solid var(--border);
        }

        .mic-action-btn.view:hover,
        .mic-action-btn.view:active {
          background: var(--info-soft);
        }

        .mic-action-btn.edit {
          color: var(--primary-solid);
          border-right: 1px solid var(--border);
        }

        .mic-action-btn.edit:hover,
        .mic-action-btn.edit:active {
          background: var(--primary-soft);
        }

        .mic-action-btn.delete {
          color: var(--danger);
        }

        .mic-action-btn.delete:hover,
        .mic-action-btn.delete:active {
          background: var(--danger-soft);
        }

        /* ═══════════════════════════════════════════════════════════════════
           TABLET LIST (576px - 767px)
        ═══════════════════════════════════════════════════════════════════ */
        .tablet-list-view {
          padding: var(--space-sm);
        }

        .tablet-inkunga-item {
          display: flex;
          gap: var(--space-sm);
          padding: var(--space-sm);
          background: var(--card);
          border: 1px solid var(--border);
          border-left: 3px solid var(--primary-solid);
          border-radius: var(--radius-md);
          margin-bottom: var(--space-xs);
          transition: all 0.2s;
        }

        .tablet-inkunga-item:hover {
          border-color: var(--primary-solid);
          border-left-color: var(--primary-solid);
          background: #f8fafc;
        }

        .tii-left {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .tii-number {
          font-size: 0.65rem;
          color: var(--muted);
          font-weight: 700;
        }

        .tii-avatar {
          width: 44px;
          height: 44px;
          border-radius: var(--radius-md);
          background: var(--primary-solid);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          font-weight: 800;
          color: white;
        }

        .tii-content {
          flex: 1;
          min-width: 0;
        }

        .tii-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--space-xs);
          margin-bottom: 0.35rem;
        }

        .tii-name {
          font-size: var(--font-sm);
          font-weight: 700;
          color: var(--text-bright);
          margin: 0;
        }

        .tii-amount {
          font-size: var(--font-sm);
          font-weight: 800;
          color: var(--success);
          white-space: nowrap;
        }

        .tii-details {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-sm);
        }

        .tii-detail {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: var(--font-xs);
          color: var(--text-secondary);
        }

        .tii-detail i {
          font-size: 0.7em;
          color: var(--muted);
        }

        .tii-actions {
          display: flex;
          flex-direction: column;
          gap: var(--space-xs);
        }

        .tii-btn {
          width: 36px;
          height: 36px;
          border-radius: var(--radius-sm);
          border: 1px solid;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tii-btn.view {
          border-color: var(--info-glow);
          color: var(--info);
        }

        .tii-btn.view:hover {
          background: var(--info-soft);
        }

        .tii-btn.edit {
          border-color: var(--primary-glow);
          color: var(--primary-solid);
        }

        .tii-btn.edit:hover {
          background: var(--primary-soft);
        }

        .tii-btn.delete {
          border-color: var(--danger-glow);
          color: var(--danger);
        }

        .tii-btn.delete:hover {
          background: var(--danger-soft);
        }

        /* ═══════════════════════════════════════════════════════════════════
           DESKTOP TABLE (768px+)
        ═══════════════════════════════════════════════════════════════════ */
        .table-responsive-wrapper {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .modern-inkunga-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .modern-inkunga-table thead {
          background: var(--primary-solid);
        }

        .modern-inkunga-table th {
          color: rgba(255,255,255,0.9);
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-size: var(--font-xs);
          padding: var(--space-md) var(--space-sm);
          border-bottom: none;
          text-align: left;
          white-space: nowrap;
          position: sticky;
          top: 0;
          z-index: 2;
        }

        .modern-inkunga-table th.th-number,
        .modern-inkunga-table th.th-actions {
          text-align: center;
        }

        .modern-inkunga-table tbody tr {
          transition: all 0.2s;
        }

        .modern-inkunga-table tbody tr:nth-child(even) {
          background: #f8fafc;
        }

        .modern-inkunga-table tbody tr:hover {
          background: var(--primary-soft);
        }

        .modern-inkunga-table td {
          padding: var(--space-sm);
          border-bottom: 1px solid var(--border);
          vertical-align: middle;
        }

        .td-number {
          text-align: center;
        }

        .row-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 9px;
          background: var(--primary-soft);
          color: var(--primary-solid);
          font-size: var(--font-xs);
          font-weight: 800;
        }

        .name-cell {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }

        .name-avatar {
          width: 40px;
          height: 40px;
          border-radius: var(--radius-md);
          background: var(--primary-solid);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          font-weight: 800;
          color: white;
          flex-shrink: 0;
        }

        .name-text {
          font-weight: 600;
          color: var(--text-bright);
          font-size: var(--font-sm);
        }

        .type-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          padding: 0.3rem 0.75rem;
          border-radius: 20px;
          font-size: var(--font-xs);
          font-weight: 700;
          color: var(--accent-solid);
          background: var(--accent-soft);
          border: 1px solid rgba(217,119,6,0.25);
        }

        .type-badge i {
          font-size: 0.65rem;
        }

        .amount-text {
          font-weight: 800;
          color: var(--success);
          font-size: var(--font-sm);
        }

        .date-text {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          color: var(--text-secondary);
          font-size: var(--font-xs);
        }

        .date-text i {
          color: var(--muted);
          font-size: 0.7rem;
        }

        .td-actions {
          text-align: center;
        }

        .action-btns {
          display: flex;
          gap: var(--space-xs);
          justify-content: center;
        }

        .action-btn {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          border: 1px solid;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.25s ease;
          font-size: 0.95rem;
        }

        .action-btn:hover {
          transform: translateY(-1px);
        }

        .action-btn.view {
          border-color: var(--info-glow);
          background: var(--info-soft);
          color: var(--info);
        }

        .action-btn.view:hover {
          background: var(--info);
          color: #fff;
        }

        .action-btn.edit {
          border-color: var(--primary-glow);
          background: var(--primary-soft);
          color: var(--primary-solid);
        }

        .action-btn.edit:hover {
          background: var(--primary-solid);
          color: #fff;
        }

        .action-btn.delete {
          border-color: var(--danger-glow);
          background: var(--danger-soft);
          color: var(--danger);
        }

        .action-btn.delete:hover {
          background: var(--danger);
          color: #fff;
        }

        /* ═══════════════════════════════════════════════════════════════════
           PAGINATION
        ═══════════════════════════════════════════════════════════════════ */
        .pagination-wrapper {
          padding: var(--space-md);
          border-top: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-sm);
        }

        .pagination-info {
          font-size: var(--font-xs);
          color: var(--muted);
        }

        .pagination-controls {
          display: flex;
          gap: 3px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .page-btn {
          min-width: 38px;
          height: 38px;
          border-radius: 10px;
          border: 1px solid var(--border);
          background: #ffffff;
          color: var(--muted);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: var(--font-sm);
          font-weight: 500;
          transition: all 0.2s;
        }

        .page-btn:hover:not(:disabled) {
          background: var(--primary-soft);
          border-color: var(--primary-solid);
          color: var(--primary-solid);
        }

        .page-btn.active {
          background: var(--primary-solid);
          border-color: var(--primary-solid);
          color: white;
          font-weight: 700;
        }

        .page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .page-ellipsis {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 30px;
          color: var(--muted);
        }

        /* ═══════════════════════════════════════════════════════════════════
           MODALS
        ═══════════════════════════════════════════════════════════════════ */
        .modal-content {
          background: var(--card) !important;
          border: 1px solid var(--border) !important;
          border-radius: var(--radius-xl) !important;
          color: var(--text) !important;
        }

        .modal-header {
          border-bottom: 1px solid var(--border) !important;
          padding: var(--space-md) !important;
        }

        .modal-title {
          font-size: var(--font-lg) !important;
          font-weight: 800;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .modal-body {
          padding: var(--space-lg) !important;
        }

        .modal-footer {
          border-top: 1px solid var(--border) !important;
          padding: var(--space-md) !important;
          gap: var(--space-sm);
        }

        .form-control,
        .form-select {
          background: #ffffff !important;
          border: 1px solid var(--border) !important;
          color: var(--text) !important;
          border-radius: var(--radius-md) !important;
          padding: var(--space-sm) !important;
          font-size: var(--font-sm) !important;
        }

        .form-control:focus,
        .form-select:focus {
          border-color: var(--primary-solid) !important;
          box-shadow: 0 0 0 3px var(--primary-soft) !important;
        }

        .form-label {
          color: var(--text-secondary) !important;
          font-size: var(--font-sm) !important;
          font-weight: 600 !important;
          margin-bottom: 0.4rem !important;
        }

        .form-control::placeholder {
          color: var(--muted) !important;
          opacity: 0.55;
        }

        /* ═══════════════════════════════════════════════════════════════════
           DELETE MODAL
        ═══════════════════════════════════════════════════════════════════ */
        .delete-modal-content {
          background: var(--card);
          border-radius: var(--radius-xl);
          border: 1px solid var(--border);
          padding: var(--space-xl);
          text-align: center;
        }

        .delete-icon-wrapper {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: var(--danger-soft);
          border: 2px solid var(--danger-solid);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto var(--space-lg);
        }

        .delete-icon-wrapper i {
          font-size: 2.25rem;
          color: var(--danger-solid);
        }

        /* ═══════════════════════════════════════════════════════════════════
           OFFCANVAS
        ═══════════════════════════════════════════════════════════════════ */
        .offcanvas {
          background: var(--bg) !important;
          border-left: 1px solid var(--border) !important;
        }

        .offcanvas-header {
          border-bottom: 1px solid var(--border);
          padding: var(--space-md);
        }

        .offcanvas-title {
          color: var(--text-bright);
          font-weight: 700;
        }

        .offcanvas-body {
          padding: var(--space-md);
        }

        /* ═══════════════════════════════════════════════════════════════════
           ALERT
        ═══════════════════════════════════════════════════════════════════ */
        .alert {
          border-radius: var(--radius-md) !important;
          border: none !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           DROPDOWN
        ═══════════════════════════════════════════════════════════════════ */
        .dropdown-menu {
          background: var(--card) !important;
          border: 1px solid var(--border) !important;
          border-radius: var(--radius-md) !important;
          padding: 0.5rem !important;
        }

        .dropdown-item {
          color: var(--text) !important;
          border-radius: var(--radius-sm) !important;
          padding: 0.5rem 1rem !important;
          transition: all 0.15s ease !important;
        }

        .dropdown-item:hover {
          background: var(--primary-soft) !important;
          color: var(--primary-solid) !important;
        }

        .dropdown-divider {
          border-color: var(--border) !important;
        }

        .btn-outline-light {
          border-color: var(--border) !important;
          color: var(--text-secondary) !important;
          border-radius: var(--radius-md) !important;
          background: #ffffff !important;
        }

        .btn-outline-light:hover {
          background: var(--primary-soft) !important;
          border-color: var(--primary-solid) !important;
          color: var(--primary-solid) !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           TOOLTIP
        ═══════════════════════════════════════════════════════════════════ */
        .tooltip-inner {
          background: var(--card) !important;
          color: var(--text) !important;
          border: 1px solid var(--border) !important;
          border-radius: 10px !important;
          font-size: var(--font-xs) !important;
          padding: 0.4rem 0.8rem !important;
          font-weight: 600 !important;
        }

        .tooltip .tooltip-arrow::before {
          border-top-color: var(--card) !important;
        }

        /* ═══════════════════════════════════════════════════════════════════
           SCROLLBAR
        ═══════════════════════════════════════════════════════════════════ */
        ::-webkit-scrollbar {
          width: 7px;
          height: 7px;
        }

        ::-webkit-scrollbar-track {
          background: var(--bg);
        }

        ::-webkit-scrollbar-thumb {
          background: var(--border-light);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: var(--muted);
        }

        * {
          scrollbar-width: thin;
          scrollbar-color: var(--border-light) var(--bg);
        }

        /* ═══════════════════════════════════════════════════════════════════
           RESPONSIVE BREAKPOINTS
        ═══════════════════════════════════════════════════════════════════ */

        /* Extra Small (< 360px) */
        @media (max-width: 359px) {
          .app-container { padding: 0.4rem; }
          .mic-actions { grid-template-columns: repeat(3, 1fr); }
          .mic-action-btn span { font-size: 0.55rem; }
        }

        /* Small Phones (360px - 479px) */
        @media (min-width: 360px) and (max-width: 479px) {}

        /* Medium Phones (480px - 575px) */
        @media (min-width: 480px) and (max-width: 575px) {
          .mobile-cards-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
          }
          .mic-actions { grid-template-columns: 1fr; }
          .mic-action-btn { flex-direction: row; justify-content: center; }
        }

        /* Mobile General (< 576px) */
        @media (max-width: 575px) {
          .mobile-filter-btn { display: flex !important; }
          .desktop-filters { display: none !important; }
          .view-toggle { display: none; }
        }

        /* Tablets (576px - 767px) */
        @media (min-width: 576px) and (max-width: 767px) {
          .mobile-filter-btn { display: flex !important; }
          .desktop-filters { display: none !important; }
          .view-toggle { display: flex; }
        }

        /* Tablets and Up (768px+) */
        @media (min-width: 768px) {
          .app-container { padding: var(--space-lg); }
          .mobile-filter-btn { display: none !important; }
          .view-toggle { display: flex; }
        }

        /* Desktop (1024px+) */
        @media (min-width: 1024px) {
          .app-container { padding: var(--space-xl) calc(var(--space-xl) * 1.5); }
        }

        /* Large Desktop (1440px+) */
        @media (min-width: 1440px) {
          .app-container { max-width: 1600px; margin: 0 auto; }
        }

        /* Touch Devices */
        @media (hover: none) and (pointer: coarse) {
          .action-btn,
          .tii-btn,
          .page-btn {
            min-width: 44px;
            min-height: 44px;
          }

          .stat-card:hover {
            transform: none;
          }

          .action-btn:hover {
            transform: none;
          }
        }

        /* Landscape Mobile */
        @media (max-width: 767px) and (orientation: landscape) {
          .mobile-cards-grid { display: grid; grid-template-columns: repeat(2, 1fr); }
        }

        /* Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Print */
        @media print {
          .app-container {
            background: white !important;
            color: black !important;
          }

          .btn-primary-custom,
          .btn-outline-custom,
          .action-btns,
          .mic-actions,
          .tii-actions,
          .pagination-wrapper,
          .header-actions,
          .mobile-filter-btn {
            display: none !important;
          }
        }
      `}</style>

      {/* Alert */}
      {alert.show && (
        <Alert
          variant={alert.type}
          dismissible
          onClose={() => setAlert({ show: false })}
          className="mb-4"
        >
          {alert.message}
        </Alert>
      )}

      {/* Header */}
      <div className="header-section">
        <div className="header-top">
          <div>
            <h1 className="header-title">
              <i className="bi bi-gift-fill" />
              Inkunga za Leta
            </h1>
            <p className="header-subtitle">
              {deviceInfo.width < 400 ? "Gucunga inkunga" : "Gucunga abaturage bahabwa inkunga za leta"}
            </p>
          </div>
          <div className="header-actions">
            <button className="btn-primary-modern" onClick={handleAdd} type="button">
              <i className="bi bi-plus-lg" />
              <span className="d-none d-sm-inline">Ongeraho</span>
            </button>
            <button className="btn-outline-modern" onClick={exportToCSV} type="button" title="Kuramo Excel">
              <i className="bi bi-download" />
              <span className="d-none d-md-inline">Excel</span>
            </button>
            <button className="btn-outline-modern" onClick={fetchRecords} type="button" title="Vugurura">
              <i className="bi bi-arrow-clockwise" />
            </button>
            <button
              className="btn-outline-modern mobile-filter-btn"
              onClick={() => setShowMobileFilters(true)}
              type="button"
              title="Filters"
            >
              <i className="bi bi-funnel" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        {statCards.map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon" style={{ "--stat-color": s.accent }}>
              <i className={`bi bi-${s.icon}`} />
            </div>
            <div className="stat-content">
              <span className="stat-value">
                {typeof s.val === "number" ? s.val.toLocaleString() : s.val}
              </span>
              <span className="stat-label">{s.title}</span>
              <span className="stat-percent" style={{ color: s.accent }}>
                {Math.min(100, Math.max(0, s.pct || 0))}% y'igiteranyo
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Filters */}
      <div className="filter-section desktop-filters d-none d-sm-block">
        <div className="filter-card">
          <div className="filter-row">
            <div className="search-wrapper">
              <i className="bi bi-search search-icon"></i>
              <input
                type="text"
                className="search-input form-control"
                placeholder="Shakisha umuturage, ubwoko..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <DropdownButton
              variant="outline-light"
              title={
                <>
                  <i className="bi bi-calendar-event me-1"></i>
                  {getFilterLabel()}
                </>
              }
            >
              <Dropdown.Item onClick={() => setFilterRange("all")}>Byose</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => setFilterRange("today")}>
                <i className="bi bi-calendar-day me-2" style={{ color: t.info }}></i>Uyu munsi
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setFilterRange("month")}>
                <i className="bi bi-calendar-month me-2" style={{ color: t.warning }}></i>Ukwezi
              </Dropdown.Item>
            </DropdownButton>

            <button className="btn-outline-custom" onClick={resetFilters}>
              <i className="bi bi-arrow-repeat"></i>
              <span className="d-none d-lg-inline">Reset</span>
            </button>
          </div>

          {(searchTerm || filterRange !== "all") && (
            <div className="filter-info">
              <i className="bi bi-info-circle me-1"></i>
              Byasanze: <strong>{filteredRecords.length}</strong> mu nkunga{" "}
              <strong>{records.length}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Filters Offcanvas */}
      <Offcanvas
        show={showMobileFilters}
        onHide={() => setShowMobileFilters(false)}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <i className="bi bi-funnel me-2"></i>Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="d-flex flex-column gap-3">
            <div>
              <Form.Label>Shakisha</Form.Label>
              <div className="search-wrapper" style={{ position: "relative" }}>
                <i className="bi bi-search search-icon"></i>
                <Form.Control
                  placeholder="Umuturage, ubwoko..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  style={{ paddingLeft: "2.25rem" }}
                />
              </div>
            </div>

            <div>
              <Form.Label>Igihe</Form.Label>
              <Form.Select
                value={filterRange}
                onChange={(e) => setFilterRange(e.target.value)}
              >
                <option value="all">Byose</option>
                <option value="today">Uyu munsi</option>
                <option value="month">Ukwezi</option>
              </Form.Select>
            </div>

            <div className="d-grid gap-2 mt-3">
              <Button
                className="btn-primary-custom"
                onClick={() => setShowMobileFilters(false)}
              >
                <i className="bi bi-check me-2"></i>Emeza
              </Button>
              <Button className="btn-outline-custom" onClick={resetFilters}>
                <i className="bi bi-arrow-repeat me-2"></i>Reset
              </Button>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Data Container */}
      <div className="data-container">
        <div className="data-header">
          <h5 className="data-title">
            <i className="bi bi-table"></i>
            <span className="d-none d-sm-inline">Urutonde rw'Inkunga</span>
            <span className="d-sm-none">Inkunga</span>
          </h5>
          <div className="data-meta">
            <span className="results-count">{filteredRecords.length} ibyabonetse</span>
            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === "auto" ? "active" : ""}`}
                onClick={() => setViewMode("auto")}
              >
                <i className="bi bi-grid-3x2-gap"></i>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "cards" ? "active" : ""}`}
                onClick={() => setViewMode("cards")}
              >
                <i className="bi bi-card-heading"></i>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                <i className="bi bi-list-ul"></i>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
              >
                <i className="bi bi-table"></i>
              </button>
            </div>
          </div>
        </div>

        {renderDataView()}
        {renderPagination()}
      </div>

      {/* Add/Edit Modal — new styled version */}
      <InkungaModal
        show={show}
        onHide={() => { setShow(false); resetForm(); }}
        editData={editing ? form : null}
        onSave={async (data) => {
          setSubmitting(true);
          try {
            if (editing) {
              await axios.put(`${API_URL}/${editId}`, {
                umuturage: data.izina,
                ubwoko: data.ubwoko,
                amount: data.agaciro,
                itariki: data.itariki,
                citizen_id: data.citizen_id || null,
              });
              showAlertMsg("Inkunga yavuguruwe neza!", "success");
            } else {
              await axios.post(API_URL, {
                umuturage: data.izina,
                ubwoko: data.ubwoko,
                amount: data.agaciro,
                itariki: data.itariki,
                citizen_id: data.citizen_id || null,
              });
              showAlertMsg("Inkunga yanditswe neza!", "success");
            }
            resetForm();
            fetchRecords();
          } catch {
            showAlertMsg("Ntibyakunze kubika. Ongera ugerageze.", "danger");
            throw new Error("save failed");
          } finally {
            setSubmitting(false);
          }
        }}
      />

      {/* Delete Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => !deleting && setShowDeleteModal(false)}
        centered
        size="sm"
      >
        <div className="delete-modal-content">
          <div className="delete-icon-wrapper">
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
          <h5 style={{ color: t.textBright, fontWeight: 800, marginBottom: "0.6rem" }}>
            Gusiba Inkunga
          </h5>
          <p style={{ color: t.textSecondary, fontSize: "0.9rem", marginBottom: "0.8rem" }}>
            Uremeza ko ushaka gusiba inkunga ya:
          </p>
          {deleteTarget && (
            <div
              style={{
                background: t.dangerSoft,
                border: `1px solid ${t.dangerGlow}`,
                borderRadius: 14,
                padding: "0.8rem",
                marginBottom: "0.8rem",
              }}
            >
              <div className="d-flex align-items-center justify-content-center gap-2">
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: t.dangerSolid,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="bi bi-person-fill" style={{ color: "#fff", fontSize: "1rem" }}></i>
                </div>
                <strong style={{ color: t.textBright }}>{deleteTarget.umuturage}</strong>
              </div>
              <div className="mt-2 d-flex justify-content-center gap-2 flex-wrap">
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: 12,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: t.success,
                    background: t.successSoft,
                  }}
                >
                  {formatRWF(deleteTarget.amount)}
                </span>
              </div>
            </div>
          )}
          <p style={{ color: t.warning, fontSize: "0.8rem", marginBottom: "1.5rem" }}>
            <i className="bi bi-exclamation-circle me-1"></i>
            Iki gikorwa ntigishobora gusubizwaho.
          </p>
          <div className="d-flex gap-2 justify-content-center flex-wrap">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              <i className="bi bi-x-circle me-1"></i>Hagarika
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={deleting}
              style={{
                background: t.dangerSolid,
                border: "none",
              }}
            >
              {deleting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Gusiba...
                </>
              ) : (
                <>
                  <i className="bi bi-trash me-1"></i>Siba
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={closeModal}
        centered
        size={deviceInfo.isMobile ? undefined : "lg"}
        fullscreen={deviceInfo.width < 576 ? true : undefined}
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{
            background: t.gradHeader,
            color: "white",
            border: "none",
          }}
        >
          <Modal.Title>
            <i className="bi bi-eye me-2"></i>Amakuru y'Inkunga
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <Row className="g-3">
              <Col xs={12}>
                <div
                  style={{
                    background: t.surface,
                    borderRadius: 16,
                    padding: "1.5rem",
                    textAlign: "center",
                    border: `1px solid ${t.border}`,
                  }}
                >
                  <div
                    style={{
                      width: 70,
                      height: 70,
                      borderRadius: 18,
                      background: t.primarySolid,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 1rem",
                      fontSize: "2rem",
                      fontWeight: 900,
                      color: "#fff",
                    }}
                  >
                    {selectedRecord.umuturage?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <h4 style={{ color: t.textBright, fontWeight: 800 }}>
                    {selectedRecord.umuturage}
                  </h4>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "6px 18px",
                      borderRadius: 20,
                      fontWeight: 700,
                      color: t.accentSolid,
                      background: t.accentSoft,
                      border: "1px solid rgba(217,119,6,0.25)",
                    }}
                  >
                    <i className="bi bi-tag-fill"></i>
                    {selectedRecord.ubwoko || "—"}
                  </span>
                </div>
              </Col>

              <Col xs={6}>
                <div
                  style={{
                    background: t.card,
                    borderRadius: 14,
                    padding: "1rem",
                    border: `1px solid ${t.border}`,
                    borderLeftWidth: 4,
                    borderLeftColor: t.successSolid,
                  }}
                >
                  <small style={{ color: t.muted, textTransform: "uppercase", fontWeight: 700, fontSize: "0.7rem" }}>
                    Agaciro
                  </small>
                  <h5 style={{ color: t.success, fontWeight: 800, marginTop: "0.25rem", marginBottom: 0 }}>
                    {formatRWF(selectedRecord.amount)}
                  </h5>
                </div>
              </Col>

              <Col xs={6}>
                <div
                  style={{
                    background: t.card,
                    borderRadius: 14,
                    padding: "1rem",
                    border: `1px solid ${t.border}`,
                    borderLeftWidth: 4,
                    borderLeftColor: t.primarySolid,
                  }}
                >
                  <small style={{ color: t.muted, textTransform: "uppercase", fontWeight: 700, fontSize: "0.7rem" }}>
                    Itariki
                  </small>
                  <h6 style={{ color: t.textBright, fontWeight: 600, marginTop: "0.25rem", marginBottom: 0 }}>
                    <i className="bi bi-calendar3 me-1" style={{ color: t.primarySolid }}></i>
                    {formatDate(selectedRecord.itariki)}
                  </h6>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              closeModal();
              if (selectedRecord) handleEdit(selectedRecord);
            }}
            style={{ background: t.primarySolid, border: "none" }}
          >
            <i className="bi bi-pencil-square me-1"></i>Hindura
          </Button>
          <Button variant="secondary" onClick={closeModal}>
            <i className="bi bi-x-circle me-1"></i>Funga
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}